import { createContext, use, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

type ApprovedResourcesContextValue = {
  // Bypasses ApprovalComponent only; the capability gate still 428s live mutations.
  isAllApproved: boolean;
  setIsAllApproved: (value: boolean) => void;
  areResourcesApproved: (paths: Set<string>) => boolean;
  approveResources: (paths: Iterable<string>) => void;
};

const ApprovedResourcesContext = createContext<ApprovedResourcesContextValue | null>(null);

// resetKey passed in rather than imported to avoid a session-provider ↔ context cycle.
type ApprovedResourcesProviderProps = { children: ReactNode; resetKey?: unknown };

export function ApprovedResourcesProvider({ children, resetKey }: ApprovedResourcesProviderProps) {
  const [isAllApproved, setIsAllApproved] = useState(false);
  const [approvedPaths, setApprovedPaths] = useState<Set<string>>(() => new Set());

  // Reset on session swap — sendCopilotPrompt and others bypass the chat input.
  // Skip the mount run; state already starts cleared, so resetting then is a wasted render.
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change -- existing effect pattern before this rule was enforced strictly; avoid adding new cases
    setIsAllApproved(false);
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change -- existing effect pattern before this rule was enforced strictly; avoid adding new cases
    setApprovedPaths(new Set());
  }, [resetKey]);

  const approveResources = useCallback((paths: Iterable<string>) => {
    setApprovedPaths((previous) => {
      const next = new Set(previous);
      let changed = false;
      for (const p of paths) {
        if (!next.has(p)) {
          next.add(p);
          changed = true;
        }
      }
      return changed ? next : previous;
    });
  }, []);

  const areResourcesApproved = useCallback(
    (paths: Set<string>) => {
      if (paths.size === 0) return true;
      for (const p of paths) {
        if (!approvedPaths.has(p)) return false;
      }
      return true;
    },
    [approvedPaths],
  );

  const value = useMemo<ApprovedResourcesContextValue>(
    () => ({ isAllApproved, setIsAllApproved, areResourcesApproved, approveResources }),
    [isAllApproved, setIsAllApproved, areResourcesApproved, approveResources],
  );

  return <ApprovedResourcesContext.Provider value={value}>{children}</ApprovedResourcesContext.Provider>;
}

export function useApprovedResources(): ApprovedResourcesContextValue {
  const context = use(ApprovedResourcesContext);
  if (context == null) {
    throw new Error('useApprovedResources must be used within an ApprovedResourcesProvider');
  }
  return context;
}
